import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  useMediaQuery,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterListIcon,
  MapOutlined,
  SearchOutlined,
  Add as AddIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import {
  getPublicServiceRequests,
  getServiceBoardFilters,
  getMapViewData,
  getProviderSettings,
  unpublishServiceRequest,
  changeQuery,
  clearError,
} from "../customerServiceBoardSlice";
import {
  PublicServiceRequest,
  ServiceBoardState,
  ServiceBoardQuery,
  StarredItem,
  MapViewQuery,
} from "../models/models";
import ServiceBoardFilters from "./ServiceBoardFilters";
import ServiceBoardContent from "./ServiceBoardContent";
import ServiceBoardMapView from "./ServiceBoardMapView";
import MapFilterPanel from "./map/MapFilterPanel";
import Loader from "../../../shared/components/Loader";
import LightTablePagination from "../../../shared/components/tables/LightTablePagination";
import ActionToast from "../../../shared/components/ActionToast";
import { NotificationMessage } from "../../../shared/models/models";
import useTranslation from "../../../shared/hooks/useTranslation";
import PermissionGuard from "../../../shared/components/PermissionGuard";
import { Modules, PermissionLevel } from "../../../core/models/PermissionModels";
import { dt, paginationWrapperSx, accentGradientDark, accentGradientDarkHover } from "../../../shared/themes/redesignTokens";
import { useRedesignSetup } from "../../../shared/hooks/useRedesignSetup";
import useDataRefresh from "../../../shared/notifications/hooks/useDataRefresh";
import useVisibilityRefresh from "../../../shared/hooks/useVisibilityRefresh";
import useChangeHighlight from "../../../shared/hooks/useChangeHighlight";
import useCompanyType from "../../../shared/hooks/useCompanyType";

// ═══════════════════════════════════════════════════════════════
//  SCROLLBAR STYLES (thin, subtle — matching MapFilterPanel)
// ═══════════════════════════════════════════════════════════════

const scrollbarSx = {
  "&::-webkit-scrollbar": { width: "5px" },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    background: "#D1D5DB",
    borderRadius: "10px",
    "&:hover": { background: "#9CA3AF" },
  },
  scrollbarWidth: "thin" as const,
  scrollbarColor: "#D1D5DB transparent",
};

// ═══════════════════════════════════════════════════════════════
//  VIEW MODE PERSISTENCE
// ═══════════════════════════════════════════════════════════════

type ViewMode = "cards" | "map";
const STORAGE_KEY = "serviceBoardViewMode";

const getInitialViewMode = (): ViewMode => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "cards" || stored === "map") return stored;
  } catch {}
  return "cards";
};

// ═══════════════════════════════════════════════════════════════
//  STARRED ITEMS PERSISTENCE
// ═══════════════════════════════════════════════════════════════

const STARRED_STORAGE_KEY = "serviceBoardStarredIds";

const getStoredStarredItems = (): StarredItem[] => {
  try {
    const stored = localStorage.getItem(STARRED_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

// ═══════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

const ServiceBoard = () => {
  const navigate = useNavigate();
  const { translate } = useTranslation(["serviceBoard", "common", "myMachinery"]);
  const dispatch = useAppDispatch();

  // 3-tier responsive: Desktop > 1200, Tablet 768–1200, Phone < 768
  const isCompact = useMediaQuery("(max-width:1200px)");
  const isPhone = useMediaQuery("(max-width:768px)");

  // Machine owner check: companyType 1 = machine owner only (no provider bit)
  const { companyType } = useCompanyType();
  const isOwnerOnly = companyType === 1;

  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(getInitialViewMode);
  const [starredItems, setStarredItems] = useState<StarredItem[]>(getStoredStarredItems);
  const [mapFilters, setMapFilters] = useState<MapViewQuery>({});

  // Search state
  const [searchValue, setSearchValue] = useState<string>("");
  const [searchFocused, setSearchFocused] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Unpublish confirmation dialog state
  const [unpublishTarget, setUnpublishTarget] = useState<PublicServiceRequest | null>(null);

  const { query, serviceRequests, filters, loading, error, mapData } =
    useAppSelector<ServiceBoardState>((state: any) => state.customerServiceBoard);

  // Real-time: track new items arriving via DataRefresh
  const [newItemsCount, setNewItemsCount] = useState(0);

  useDataRefresh("serviceBoard", (event) => {
    if (event.action === "removed") {
      // Silently refresh the board — no banner for removed/unpublished requests
      dispatch(getPublicServiceRequests(query));
      dispatch(getMapViewData({}));
    } else {
      // "new" or other actions — show the banner
      setNewItemsCount((prev) => prev + 1);
    }
  });

  useVisibilityRefresh(() => {
    dispatch(getPublicServiceRequests(query));
    dispatch(getMapViewData({}));
    setNewItemsCount(0);
  });

  useRedesignSetup();

  // Change highlight: track which service requests changed on silent refresh
  const changedIds = useChangeHighlight(
    serviceRequests?.items ?? [],
    (item) => item.id,
    (item) => `${item.statusCode}-${item.issue}-${item.priorityTypeCode}-${item.offersCount}`,
  );

  // Page-specific keyframes (map animations)
  useEffect(() => {
    const styleId = "sb-map-keyframes";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        @keyframes markerPulse {
          0% { transform: scale(1); opacity: 0.6; }
          50% { transform: scale(1.35); opacity: 0; }
          100% { transform: scale(1); opacity: 0; }
        }
        @keyframes mapFadeIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes slideInRight {
          0% { opacity: 0; transform: translateX(20px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        @keyframes slideUpSheet {
          0% { opacity: 0; transform: translateY(100%); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // --- Initial data load ---
  useEffect(() => {
    setSearchValue(query.search || "");
    dispatch(getPublicServiceRequests(query));
    dispatch(getServiceBoardFilters());
    // Fetch map summary data for stats bar (lightweight endpoint)
    dispatch(getMapViewData({}));
    dispatch(getProviderSettings());
  }, []);

  // ═══════════════════════════════════════════════════════════
  //  EVENT HANDLERS — unchanged logic
  // ═══════════════════════════════════════════════════════════

  const handleQueryChange = (newQuery: ServiceBoardQuery) => {
    dispatch(changeQuery(newQuery));
    dispatch(getPublicServiceRequests(newQuery));
  };

  const handleSearch = useCallback(
    (value?: string) => {
      const newQuery = { ...query, search: value, pageNo: 1 };
      handleQueryChange(newQuery);
    },
    [query]
  );

  const handleSearchInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchValue(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (value.trim().length === 0) {
        handleSearch(undefined);
        return;
      }
      debounceRef.current = setTimeout(() => {
        handleSearch(value || undefined);
      }, 400);
    },
    [handleSearch]
  );

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        handleSearch(searchValue || undefined);
      }
    },
    [searchValue, handleSearch]
  );


  const handlePageSizeChange = (newPageSize: number) => {
    const newQuery = { ...query, pageNo: 1, pageSize: newPageSize };
    handleQueryChange(newQuery);
  };

  const handlePageNoChange = (newPage: number) => {
    const newQuery = { ...query, pageNo: newPage };
    handleQueryChange(newQuery);
  };

  const handleViewDetails = (serviceRequest: PublicServiceRequest) => {
    navigate(`/service-board/${serviceRequest.id}`);
  };

  const handleUnpublishRequest = (serviceRequest: PublicServiceRequest) => {
    setUnpublishTarget(serviceRequest);
  };

  const handleConfirmUnpublish = async () => {
    if (!unpublishTarget) return;
    try {
      await dispatch(unpublishServiceRequest(unpublishTarget.id)).unwrap();
    } catch {
      // error handled by slice
    }
    setUnpublishTarget(null);
  };

  const handleMakeOffer = (serviceRequest: PublicServiceRequest) => {
    navigate(`/service-offers/new/${serviceRequest.id}`);
  };

  const handleBookmark = (serviceRequest: PublicServiceRequest) => {
    setStarredItems((prev) => {
      const exists = prev.some((item) => item.id === serviceRequest.id);
      const next = exists
        ? prev.filter((item) => item.id !== serviceRequest.id)
        : [...prev, { id: serviceRequest.id, internalNumber: serviceRequest.internalNumber }];
      try {
        localStorage.setItem(STARRED_STORAGE_KEY, JSON.stringify(next));
      } catch {}
      return next;
    });
  };

  const handleRemoveStarred = (id: string) => {
    setStarredItems((prev) => {
      const next = prev.filter((item) => item.id !== id);
      try {
        localStorage.setItem(STARRED_STORAGE_KEY, JSON.stringify(next));
      } catch {}
      return next;
    });
  };

  const handleMapFilterChange = useCallback((newFilters: MapViewQuery) => {
    setMapFilters(newFilters);
  }, []);

  const handleRefreshNewItems = useCallback(() => {
    dispatch(getPublicServiceRequests(query));
    dispatch(getMapViewData({}));
    setNewItemsCount(0);
  }, [dispatch, query]);

  const handleOpenMap = useCallback(() => {
    setViewMode("map");
    try {
      localStorage.setItem(STORAGE_KEY, "map");
    } catch {}
  }, []);

  if (loading && !serviceRequests) {
    return <Loader />;
  }


  const starredIds = starredItems.map((item) => item.id);
  const allItems = serviceRequests?.items ?? [];
  const displayedRequests = query.starredOnly
    ? allItems.filter((sr) => starredIds.includes(sr.id))
    : allItems;

  const activeFilterCount =
    (query.urgentOnly ? 1 : 0) +
    (query.starredOnly ? 1 : 0) +
    (query.dateFrom ? 1 : 0) +
    (query.dateTo ? 1 : 0) +
    (query.machineBrandIds?.length ?? 0) +
    (query.priorityTypeCodes?.length ?? 0) +
    (query.serviceTypeCodes?.length ?? 0) +
    (query.countryIds?.length ?? 0);

  // ═══════════════════════════════════════════════════════════
  //  SHARED: Search input (used in sidebar & accordion)
  // ═══════════════════════════════════════════════════════════

  const renderSearchInput = () => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        background: dt.inputBg,
        borderRadius: dt.radiusSm,
        border: `1.5px solid ${searchFocused ? dt.blue : "transparent"}`,
        padding: "0 12px",
        height: "40px",
        transition: "all 0.25s ease",
        boxShadow: searchFocused ? `0 0 0 3px ${dt.blueGlow}` : "none",
        mb: "16px",
      }}
    >
      <SearchOutlined
        sx={{
          fontSize: "16px",
          color: searchFocused ? dt.blue : dt.textMuted,
          flexShrink: 0,
          transition: "color 0.2s",
        }}
      />
      <input
        type="text"
        value={searchValue}
        onChange={handleSearchInputChange}
        onKeyDown={handleSearchKeyDown}
        onFocus={() => setSearchFocused(true)}
        onBlur={() => setSearchFocused(false)}
        placeholder={translate("common:buttons.search") + "..."}
        style={{
          border: "none",
          outline: "none",
          width: "100%",
          fontSize: "13px",
          fontFamily: 'var(--font-sans)',
          color: dt.textPrimary,
          background: "transparent",
          marginLeft: "8px",
        }}
      />
    </Box>
  );

  // ═══════════════════════════════════════════════════════════
  //  SHARED: Filter content (used in sidebar & accordion)
  // ═══════════════════════════════════════════════════════════

  const renderFilterContent = () => (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <Box sx={{ flexShrink: 0 }}>
        {renderSearchInput()}
        <Box sx={{ borderTop: `1px solid ${dt.cardBorder}`, mb: "12px" }} />
      </Box>
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          ...scrollbarSx,
        }}
      >
        <ServiceBoardFilters
          filters={filters}
          query={query}
          starredItems={starredItems}
          onQueryChange={handleQueryChange}
          onRemoveStarred={handleRemoveStarred}
          onOpenMap={handleOpenMap}
        />
      </Box>
    </Box>
  );

  const renderMapFilterContent = () => (
    <MapFilterPanel
      mapData={mapData}
      onFilterChange={handleMapFilterChange}
    />
  );

  // ═══════════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════════

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        backgroundColor: dt.pageBg,
        backgroundImage: `radial-gradient(circle at 1px 1px, ${dt.pageDotColor} 1px, transparent 0)`,
        backgroundSize: "28px 28px",
        fontFamily: dt.font,
        overflowX: "hidden",
        px: { xs: "16px", md: "32px" },
        "& *:not(.MuiSvgIcon-root)": { fontFamily: `${dt.font}` },
      }}
    >
      <ActionToast
        open={!!error}
        variant="error"
        message={translate(NotificationMessage.error(error).message)}
        onClose={() => dispatch(clearError())}
        duration={6000}
      />

      {/* ═══════════════════════════════════════════════════════
          MAIN CONTENT
          ═══════════════════════════════════════════════════════ */}
      <Box
        sx={{
          display: "flex",
          gap: "24px",
          pt: { xs: 2, md: 3 },
          pb: 3,
          minWidth: 0,
          height: "calc(100vh - 110px)",
          alignItems: "stretch",
          overflow: "hidden",
        }}
      >
        {/* ═════════════════════════════════════════════════════
            DESKTOP SIDEBAR (>1200px only)
            ═════════════════════════════════════════════════════ */}
        {!isCompact && (
          <Box
            sx={{
              width: viewMode === "map" ? "220px" : "260px",
              minWidth: viewMode === "map" ? "220px" : "260px",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              animation: "fadeLeft 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.1s both",
              transition: "width 0.25s ease, min-width 0.25s ease",
              height: "100%",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                backgroundColor: dt.cardBg,
                borderRadius: dt.radiusLg,
                border: `1px solid ${dt.cardBorder}`,
                padding: viewMode === "map" ? "16px" : "16px",
                boxShadow: viewMode === "map"
                  ? "4px 0 20px rgba(0,0,0,0.03)"
                  : dt.cardShadow,
                borderRight: `1px solid ${dt.cardBorder}`,
                height: "100%",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {viewMode === "map" ? renderMapFilterContent() : renderFilterContent()}
            </Box>
          </Box>
        )}

        {/* ═════════════════════════════════════════════════════
            MAIN AREA
            ═════════════════════════════════════════════════════ */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            gap: "16px",
            overflow: "hidden",
            animation: "fadeUp 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s both",
          }}
        >
          {/* ═══════════════════════════════════════════════════
              ACCORDION FILTERS (tablet & phone — compact mode)
              ═══════════════════════════════════════════════════ */}
          {isCompact && (
            <Accordion
              expanded={filtersExpanded}
              onChange={() => setFiltersExpanded(!filtersExpanded)}
              sx={{
                backgroundColor: dt.cardBg,
                borderRadius: `${dt.radiusSm} !important`,
                border: `1px solid ${dt.cardBorder}`,
                boxShadow: "none !important",
                "&:before": { display: "none" },
                "&.Mui-expanded": { margin: "0 !important" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: dt.textSecondary }} />}
                sx={{
                  minHeight: "48px",
                  px: 2,
                  "&.Mui-expanded": { minHeight: "48px" },
                  "& .MuiAccordionSummary-content": {
                    alignItems: "center",
                    gap: 1,
                    my: "10px",
                    "&.Mui-expanded": { my: "10px" },
                  },
                }}
              >
                <FilterListIcon sx={{ color: dt.textMuted, fontSize: "20px" }} />
                <Box
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: dt.textSecondary,
                    fontFamily: dt.font,
                  }}
                >
                  {translate("common:filter")}
                </Box>
                {activeFilterCount > 0 && (
                  <Badge
                    badgeContent={activeFilterCount}
                    sx={{
                      ml: 1,
                      "& .MuiBadge-badge": {
                        backgroundColor: dt.blue,
                        color: "#fff",
                        fontSize: "10px",
                        fontFamily: dt.fontMono,
                        minWidth: "18px",
                        height: "18px",
                      },
                    }}
                  />
                )}
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
                {viewMode === "map" ? renderMapFilterContent() : renderFilterContent()}
              </AccordionDetails>
            </Accordion>
          )}

          {/* ═══════════════════════════════════════════════════
              NEW ITEMS BANNER (real-time push-to-refresh)
              ═══════════════════════════════════════════════════ */}
          {newItemsCount > 0 && viewMode !== "map" && (
            <Alert
              severity="info"
              icon={<RefreshIcon sx={{ fontSize: 20 }} />}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleRefreshNewItems}
                  sx={{
                    fontFamily: dt.font,
                    fontWeight: 600,
                    fontSize: "12px",
                    textTransform: "none",
                  }}
                >
                  {translate("common:buttons.refresh") || "Refresh"}
                </Button>
              }
              sx={{
                borderRadius: dt.radiusSm,
                fontFamily: dt.font,
                fontSize: "13px",
                animation: "fadeUp 0.3s ease both",
                "& .MuiAlert-message": { fontFamily: dt.font },
              }}
            >
              {newItemsCount === 1
                ? translate("newRequestAvailable") || "1 new request available"
                : (translate("newRequestsAvailable") || "{count} new requests available").replace(
                    "{count}",
                    String(newItemsCount)
                  )}
            </Alert>
          )}

          {/* ═══════════════════════════════════════════════════
              STATS BAR + VIEW TOGGLE + CREATE BUTTON (cards + table modes)
              ═══════════════════════════════════════════════════ */}
          {viewMode !== "map" && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1.5,
                px: "16px",
                py: "10px",
                backgroundColor: dt.cardBg,
                borderRadius: dt.radiusSm,
                border: `1px solid ${dt.cardBorder}`,
                boxShadow: dt.cardShadow,
                flexShrink: 0,
                animation: "headerReveal 0.4s ease 0.1s both",
                ...(isPhone && {
                  gap: 1,
                  px: "12px",
                  py: "8px",
                }),
              }}
            >
              {/* Left: stats */}
              {mapData?.summary && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
                  <StatPill
                    label={translate("mapLocations")}
                    value={mapData.summary.totalLocations}
                    color={dt.blueHover}
                  />
                  <StatPill
                    label={translate("mapRequests")}
                    value={mapData.summary.totalRequests}
                    color="#27AE60"
                  />
                  <StatPill
                    label={translate("mapUrgent")}
                    value={mapData.summary.urgentRequests}
                    color="#C0392B"
                  />
                </Box>
              )}

              {/* Right: map view button + create button */}
              <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0, ml: "auto", flexWrap: "wrap", justifyContent: "flex-end" }}>
                {/* Map View button */}
                <Button
                  variant="outlined"
                  startIcon={<MapOutlined sx={{ fontSize: "16px !important" }} />}
                  onClick={() => { setViewMode("map"); try { localStorage.setItem(STORAGE_KEY, "map"); } catch {} }}
                  sx={{
                    borderRadius: dt.radiusPill,
                    borderColor: dt.cardBorder,
                    color: dt.textSecondary,
                    fontFamily: dt.font,
                    fontWeight: 600,
                    fontSize: "12px",
                    textTransform: "none",
                    padding: "5px 14px",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: dt.blue,
                      color: dt.blue,
                      backgroundColor: dt.blueGlow,
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  {translate("mapViewButton")}
                </Button>
                {/* Create Request button -- Write+ only */}
                <PermissionGuard module={Modules.Servicing} level={PermissionLevel.Write}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon sx={{ fontSize: "16px !important" }} />}
                    onClick={() => navigate("/service-requests/new")}
                    sx={{
                      background: accentGradientDark,
                      borderRadius: dt.radiusPill,
                      padding: isPhone ? "6px 14px" : "6px 18px",
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "12px",
                      fontFamily: dt.font,
                      letterSpacing: "0.2px",
                      boxShadow: "0 2px 8px rgba(52,152,219,0.25)",
                      border: "none",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                      minWidth: "auto",
                      lineHeight: 1.5,
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      ...(isPhone && { width: "100%", order: 10 }),
                      "&:hover": {
                        background: accentGradientDarkHover,
                        transform: "translateY(-1px)",
                        boxShadow: "0 4px 12px rgba(52,152,219,0.3)",
                      },
                      "&:active": {
                        transform: "translateY(0)",
                        boxShadow: "0 2px 8px rgba(52,152,219,0.25)",
                      },
                    }}
                  >
                    {translate("myMachinery:createRequest")}
                  </Button>
                </PermissionGuard>
              </Box>
            </Box>
          )}

          {/* ═══════════════════════════════════════════════════
              VIEW CONTENT
              ═══════════════════════════════════════════════════ */}
          {viewMode === "map" ? (
            /* Map mode */
            <ServiceBoardMapView
              filters={mapFilters}
              onClose={() => {
                setViewMode("cards");
                try { localStorage.setItem(STORAGE_KEY, "cards"); } catch {}
              }}
            />
          ) : loading && !displayedRequests.length ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: dt.cardBg,
                borderRadius: dt.radiusLg,
                border: `1px solid ${dt.cardBorder}`,
                boxShadow: dt.cardShadow,
                minHeight: "400px",
                flex: 1,
              }}
            >
              <Loader />
            </Box>
          ) : (
            /* Card mode — with page-locked scroll */
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                borderRadius: dt.radiusSm,
                backgroundColor: dt.cardBg,
                border: `1px solid ${dt.cardBorder}`,
                boxShadow: dt.cardShadow,
                flex: 1,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  overflowX: "hidden",
                  padding: { xs: 1, md: 2 },
                  ...scrollbarSx,
                }}
              >
                <ServiceBoardContent
                  serviceRequests={displayedRequests}
                  starredIds={starredIds}
                  onViewDetails={handleViewDetails}
                  onUnpublish={handleUnpublishRequest}
                  onMakeOffer={handleMakeOffer}
                  onBookmark={handleBookmark}
                  changedIds={changedIds}
                  isOwnerOnly={isOwnerOnly}
                />
              </Box>
              {serviceRequests && serviceRequests.items.length > 0 && (
                <Box
                  sx={{
                    flexShrink: 0,
                    borderTop: `1px solid ${dt.cardBorder}`,
                    px: { xs: 1, md: 2 },
                    ...paginationWrapperSx,
                  }}
                >
                  <LightTablePagination
                    totalCount={serviceRequests.totalCount}
                    currentPage={query.pageNo}
                    pageSize={query.pageSize}
                    pageSizeOptions={[10, 20, 50, 100]}
                    onPageChange={handlePageNoChange}
                    onPageSizeChange={handlePageSizeChange}
                    rowsPerPageLabel={translate("common:rowsPerPage")}
                    ofLabel={translate("common:of")}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* ═══════════════════════════════════════════════════════
          UNPUBLISH CONFIRMATION DIALOG
          ═══════════════════════════════════════════════════════ */}
      <Dialog
        open={!!unpublishTarget}
        onClose={() => setUnpublishTarget(null)}
        PaperProps={{
          sx: {
            borderRadius: dt.radiusLg,
            fontFamily: dt.font,
            maxWidth: "440px",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: dt.font,
            fontWeight: 700,
            fontSize: "18px",
            color: dt.textPrimary,
            pb: 1,
          }}
        >
          {translate("unpublishDialogTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              fontFamily: dt.font,
              fontSize: "14px",
              color: dt.textSecondary,
              lineHeight: 1.6,
            }}
          >
            {translate("unpublishDialogMessage", {
              number: unpublishTarget?.internalNumber || "",
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setUnpublishTarget(null)}
            sx={{
              fontFamily: dt.font,
              fontWeight: 600,
              fontSize: "13px",
              textTransform: "none",
              color: dt.textSecondary,
              borderRadius: dt.radiusSm,
            }}
          >
            {translate("common:buttons.cancel")}
          </Button>
          <Button
            onClick={handleConfirmUnpublish}
            variant="contained"
            sx={{
              fontFamily: dt.font,
              fontWeight: 600,
              fontSize: "13px",
              textTransform: "none",
              borderRadius: dt.radiusSm,
              backgroundColor: "#E67E22",
              "&:hover": { backgroundColor: "#D35400" },
            }}
          >
            {translate("unpublishConfirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════
//  STAT PILL (inline helper — matching map view)
// ═══════════════════════════════════════════════════════════════

interface StatPillProps {
  label: string;
  value: number;
  color: string;
}

const StatPill: React.FC<StatPillProps> = React.memo(({ label, value, color }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
    <Box
      sx={{
        fontSize: "11px",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.6px",
        color: "#9CA3AF",
        fontFamily: dt.font,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
    <Box
      sx={{
        fontSize: "16px",
        fontWeight: 700,
        fontFamily: dt.fontMono,
        color: color,
        letterSpacing: "-0.3px",
      }}
    >
      {value}
    </Box>
  </Box>
));

export default ServiceBoard;
